import { useQuery } from '@tanstack/react-query'
import { graphqlClient } from '@/lib/graphql_client'
import { useContextStore } from '@/lib/session/context-store'

// ENT-360: these operations belong in src/graphql/operations/agency-approval-status.graphql
// and should use fetchGql(AgencyApprovalStatusDocument, {}). The inline form is a temporary
// workaround: codegen is blocked by 73 pre-existing BE contract gaps (T0004). Both
// operations are schema-valid (myContexts exists in the gateway). Remove the eslint-disable
// comments once codegen can run cleanly and the Document exports are generated.

// eslint-disable-next-line no-restricted-syntax -- ENT-360: inline GQL temporary; codegen blocked by 73 BE gaps (T0004)
const AGENCY_APPROVAL_STATUS_QUERY = `
  query AgencyApprovalStatus {
    myContexts {
      persona
      approvalStatus
      tenantId
    }
  }
`

interface ContextRow {
  persona: string
  approvalStatus: string | null
  tenantId: string
}

interface AgencyApprovalStatusData {
  myContexts: ContextRow[]
}

export type ApprovalStatus = 'pending' | 'approved' | 'declined'

const VALID_STATUSES = new Set<string>(['pending', 'approved', 'declined'])

export function useAgencyStatus(opts?: { enabled?: boolean }) {
  const activeTenantId = useContextStore((s) => s.active?.tenantId)

  const query = useQuery<AgencyApprovalStatusData>({
    queryKey: ['agency', 'approvalStatus', activeTenantId],
    queryFn: () =>
      // eslint-disable-next-line no-restricted-syntax -- ENT-360: inline GQL temporary; codegen blocked by 73 BE gaps (T0004)
      graphqlClient.request<AgencyApprovalStatusData>(AGENCY_APPROVAL_STATUS_QUERY),
    // Poll only while the outcome can still change. DECLINED and APPROVED are terminal: a declined
    // agency previously re-queried every 30s forever, which PRD §5 AC3 names explicitly ("is told
    // clearly and is not left polling forever"). The consumer passes `enabled: isAgencyPersona`
    // (ReadinessGate), which stays true after a decline — so the stop has to live here.
    refetchInterval: (query) => {
      const status = query.state.data?.myContexts
        ?.find((c) => c.persona === 'AGENCY' && c.tenantId === activeTenantId)
        ?.approvalStatus?.toLowerCase()
      return status === 'declined' || status === 'approved' ? false : 30_000
    },
    retry: false,
    enabled: (opts?.enabled ?? true) && !!activeTenantId,
  })

  const agencyContext = query.data?.myContexts?.find(
    (c) => c.persona === 'AGENCY' && c.tenantId === activeTenantId,
  )
  const rawStatus = agencyContext?.approvalStatus?.toLowerCase() ?? ''
  const approvalStatus: ApprovalStatus = VALID_STATUSES.has(rawStatus)
    ? (rawStatus as ApprovalStatus)
    : 'pending'

  return { ...query, approvalStatus }
}
